<template>
  <div>
    <!-- <MButton type="primary" color="purple" @click="showPivotModal">{{ t('common.pivot') }}</MButton> -->
    <BasicModal
      width="100vw"
      :height="windowSize.height - 150"
      :footer="null"
      v-bind="$attrs"
      destroyOnClose
      @cancel="pivotCache.save"
      @register="registerModal"
    >
      <template #title>
        <BasicTitle>
          {{ title }}
          <SyncOutlined class="ml-10px text-14px" @click="loadReport(false)" />
        </BasicTitle>
      </template>

      <div class="px-12px pt-0">
        <Spin :spinning="loading" tip="数据加载中...">
          <Pivot
            ref="pivotRef"
            :height="windowSize.height - 160"
            toolbar
            :report="report"
            :beforetoolbarcreated="customizeToolbar"
          />
        </Spin>
      </div>
    </BasicModal>
  </div>
</template>

<script setup lang="ts">
  import { SyncOutlined } from '@ant-design/icons-vue';
  import { Spin } from 'ant-design-vue';
  import { onMounted, reactive, ref, useAttrs } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { createAsyncComponent } from '@mts-fe/utils';
  import { BasicTitle } from '@mts/components/Basic';
  import { BasicModal, useModalInner } from '@mts/components/Modal';
  import { TableActionType } from '@mts/components/Table';
  import { t } from '@mts/hooks/web/useI18n';
  import { BaseApiFn, RenderTableColumn } from '@mts/render';
  import { windowSize } from '@mts/utils';
  import API, { parseConfigValue } from '@/api/ims-system';

  const Pivot = createAsyncComponent(() => import('./Pivot.vue'));

  const props = withDefaults(
    defineProps<{
      /** 数据接口API */
      fetchApi: BaseApiFn;
      /** 缓存key，默认为当前路由 */
      cacheKey?: string;
      /** 缓存key，请求数据size */
      size?: number;

      getSearchInfo?: TableActionType['getSearchInfo'];
      getColumns?: TableActionType['getColumns'];
      /** 数据处理回调函数，允许父组件在数据添加前处理数据 */
      dataProcessor?: (data: any[]) => any[];
    }>(),
    { size: 1e3 },
  );

  const emit = defineEmits(['register']);
  // function customizeCellFunction(cellBuilder, cellData) {
  //   console.log(cellBuilder, cellData);
  //   if (cellData.type == 'value') {
  //     if (cellData.rowIndex % 2 == 0) {
  //       cellBuilder.addClass('alter1');
  //     } else {
  //       cellBuilder.addClass('alter2');
  //     }
  //   }
  // }

  const router = useRouter();
  const pivotCache = {
    cacheKey:
      'pivot:' + (props.cacheKey || router.currentRoute.value.fullPath.replace(/[/\\]/g, '_')),
    cacheData: undefined as any,
    load: () => {
      API.getSysConfig({ configKey: pivotCache.cacheKey })
        .then(parseConfigValue)
        .then((res) => {
          if (typeof res !== 'object') return;
          pivotCache.cacheData = res;
        });
    },
    save: () => {
      const _report = pivotRef.value?.getReport();
      if (!_report.options) {
        _report.options = { grid: { type: 'compact', showTotals: 'off' } };
      }
      delete _report.dataSource;
      pivotCache.cacheData = _report;
      API.saveSysConfig({
        usageScene: 'pivot',
        configKey: pivotCache.cacheKey,
        configValue: JSON.stringify(_report),
      });
    },
  };
  onMounted(pivotCache.load);

  const pivotRef = ref();
  const loading = ref(false);
  const report = reactive({
    options: {
      grid: { type: 'classic', showTotals: 'off' },
    },
    slice: { expands: { expandAll: true } } as Recordable,
    dataSource: { data: [] as any[] },
    conditions: [],
    formats: [
      {
        name: '',
        thousandsSeparator: '',
        decimalSeparator: '.',
        currencySymbol: '',
        currencySymbolAlign: 'left',
        nullValue: '',
        textAlign: 'right',
        isPercent: false,
      },
    ],
  });
  const loadData = async (params) => {
    if (!getOpen?.value) {
      return;
    }

    loading.value = true;
    try {
      const res = await props.fetchApi({ size: props.size, ...params, page: params.page || 1 });
      // console.log(res);
      const processedData = props.dataProcessor ? props.dataProcessor(res.data) : res.data;
      report.dataSource.data.push(...processedData);
      // report.dataSource.data.push(...res.data);

      if (res.total > res.page * res.size) {
        // loading.value = false;
        loadData({ ...params, page: res.page + 1 });
      } else {
        loading.value = false;
        pivotRef.value?.setReport(report);
      }
    } catch (e) {
      console.error(e);
      loading.value = false;
      report.dataSource.data = [];
    }
  };

  const generateSlice = (listData: any[]) => {
    const reportFilters = listData
      .filter((i) => i.pivot?.slice === 'filter')
      .map((i) => ({ uniqueName: i.dataIndex }));
    const rows = listData
      .filter((i) => i.pivot?.slice === 'row')
      .map((i) => ({ uniqueName: i.dataIndex }));
    const columns = listData
      .filter((i) => i.pivot?.slice === 'column')
      .map((i) => ({ uniqueName: i.dataIndex }))
      .concat({ uniqueName: 'Measures' });
    const col = listData
      .filter((i) => i.pivot?.slice === 'col')
      .map((i) => ({ uniqueName: i.dataIndex }));
    const measures = listData
      .filter((i) => i.pivot?.slice === 'measure')
      .map((i) => ({ uniqueName: i.dataIndex, aggregation: 'sum', format: i.pivot.format }));
    return { reportFilters, rows, columns: [...columns, ...col], measures };
  };

  const curPayload = ref<{
    columns: RenderTableColumn[];
    fetchParams: Recordable;
    customSlice?: (slice: Recordable) => Recordable;
  }>({
    columns: [],
    fetchParams: {},
  });

  // const [registerPivot, { openModal: openPivotModal }] = useModal();
  // const showPivotModal = () =>
  //   openPivotModal(true, {
  //     fetchParams: { ...getSearchInfo(), size: 1e3 },
  //     columns: getColumns({ ignoreIndex: true, ignoreAction: true }),
  //   });

  const [registerModal, { getOpen }] = useModalInner(async (payload) => {
    curPayload.value = { ...payload };
    loadReport();
  });

  const loadReport = (useCache = true) => {
    const { columns, fetchParams, customSlice } = curPayload.value;
    const dataTypes = columns.reduce((acc, cur) => {
      const title = cur.title as any;
      const caption = typeof title === 'string' ? t(title) : (title as string);
      return { ...acc, [cur.dataIndex as string]: { caption, ...cur.pivot } };
    }, {});
    report.dataSource.data = [dataTypes];

    if (useCache && pivotCache.cacheData) {
      Object.assign(report, pivotCache.cacheData);
    } else {
      report.slice = { ...report.slice, ...generateSlice(columns) } as any;
    }
    report.formats?.push({
      name: 'number',
      thousandsSeparator: ',',
      decimalSeparator: '.',
      currencySymbol: '',
      currencySymbolAlign: 'left',
      nullValue: '',
      textAlign: 'right',
      isPercent: false,
    });
    if (customSlice) {
      report.slice = customSlice(report.slice);
    }
    pivotRef.value?.setReport(report);
    loadData(fetchParams);
  };

  const route = useRoute();
  const { title = t(route.name as string) || '透视表' } = useAttrs();
  const customizeToolbar = (toolbar) => {
    var tabs = toolbar.getTabs();
    console.log(tabs, toolbar);
    toolbar.getTabs = function () {
      delete tabs[0];
      delete tabs[1];
      delete tabs[2];
      tabs[3].handler = () =>
        pivotRef.value?.exportTo('excel', { filename: title, excelSheetName: title });
      delete tabs[3].menu;
      delete tabs[7];
      return tabs;
    };
  };
</script>
